import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import DictationInput from '../components/DictationInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { BookOpen, Plus, Calendar, Users, GraduationCap, Brain, Heart, Scale } from 'lucide-react';

const typeIcons = { iop: Heart, cognitive_change: Brain, psychoeducation: GraduationCap, criminogenic: Scale, general: BookOpen };
const typeLabels = { iop: 'Intensive Outpatient', cognitive_change: 'Cognitive Change', psychoeducation: 'Psychoeducation', criminogenic: 'Criminogenic / Re-Entry', general: 'General' };
const typeColors = { iop: 'bg-teal-50 text-teal-700', cognitive_change: 'bg-sky-50 text-sky-700', psychoeducation: 'bg-amber-50 text-amber-700', criminogenic: 'bg-violet-50 text-violet-700', general: 'bg-slate-50 text-slate-700' };

export default function ProgramsPage() {
  const { user, axiosAuth } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [services, setServices] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', program_type: 'general', description: '', schedule: '', capacity: 20, subtypes: [] });
  const api = axiosAuth();
  const canCreate = ['coordinator', 'admin'].includes(user.role);

  useEffect(() => {
    api.get('/programs').then(r => setPrograms(r.data)).catch(e => console.error('Programs fetch error:', e));
    api.get('/programs/services-overview').then(r => setServices(r.data)).catch(e => console.error('Services fetch error:', e));
  }, []);

  const handleCreate = async () => {
    if (!form.name) return toast.error('Program name required');
    try {
      await api.post('/programs', form);
      toast.success('Program created!');
      setOpen(false);
      setForm({ name: '', program_type: 'general', description: '', schedule: '', capacity: 20, subtypes: [] });
      api.get('/programs').then(r => setPrograms(r.data));
    } catch { toast.error('Failed to create program'); }
  };

  return (
    <div className="space-y-6" data-testid="programs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Programs & Services</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {user.role === 'court_officer' ? 'View available services and programs' : 'Manage program offerings'}
          </p>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2" data-testid="new-program-button"><Plus size={16} /> New Program</Button></DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>Create Program</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="space-y-1.5"><Label>Program Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Program name" data-testid="program-name-input" /></div>
                <div className="space-y-1.5"><Label>Type</Label>
                  <Select value={form.program_type} onValueChange={v => setForm({...form, program_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="iop">Intensive Outpatient (IOP)</SelectItem>
                      <SelectItem value="cognitive_change">Cognitive Change Group</SelectItem>
                      <SelectItem value="psychoeducation">Psychoeducation</SelectItem>
                      <SelectItem value="criminogenic">Criminogenic / Re-Entry</SelectItem>
                      <SelectItem value="general">General</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Description</Label><DictationInput value={form.description} onChange={(val) => setForm({...form, description: val})} rows={3} showCleanup={false} /></div>
                <div className="space-y-1.5"><Label>Schedule</Label><Input value={form.schedule} onChange={e => setForm({...form, schedule: e.target.value})} placeholder="e.g., Mon/Wed/Fri 9AM-12PM" /></div>
                <div className="space-y-1.5"><Label>Capacity</Label><Input type="number" value={form.capacity} onChange={e => setForm({...form, capacity: parseInt(e.target.value)})} /></div>
                <Button onClick={handleCreate} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="create-program-button">Create Program</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Summary Stats */}
      {services && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-4">
            <p className="text-2xl font-bold">{services.total_programs}</p>
            <p className="text-xs text-muted-foreground">Active Programs</p>
          </CardContent></Card>
          <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-4">
            <p className="text-2xl font-bold">{services.total_groups}</p>
            <p className="text-xs text-muted-foreground">Group Sessions</p>
          </CardContent></Card>
          <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-4">
            <p className="text-2xl font-bold">{Object.keys(services.programs_by_type || {}).length}</p>
            <p className="text-xs text-muted-foreground">Program Types</p>
          </CardContent></Card>
          <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-4">
            <p className="text-2xl font-bold">{services.upcoming_groups?.length || 0}</p>
            <p className="text-xs text-muted-foreground">Upcoming Groups</p>
          </CardContent></Card>
        </div>
      )}

      {/* Programs List */}
      <div className="space-y-3">
        {programs.length > 0 ? programs.map(p => {
          const Icon = typeIcons[p.program_type] || BookOpen;
          return (
            <Card key={p.id} className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${typeColors[p.program_type]?.split(' ')[0] || 'bg-muted'}`}>
                    <Icon className={`w-6 h-6 ${typeColors[p.program_type]?.split(' ')[1] || 'text-muted-foreground'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold">{p.name}</p>
                      <Badge className={typeColors[p.program_type] || 'bg-muted'} variant="secondary">{typeLabels[p.program_type]}</Badge>
                      {p.active && <Badge className="bg-emerald-50 text-emerald-700" variant="secondary">Active</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">{p.description}</p>
                    {p.schedule && (
                      <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                        <Calendar size={12} /> {p.schedule}
                      </div>
                    )}
                    <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                      <Users size={12} /> Capacity: {p.capacity}
                    </div>
                    {p.subtypes?.length > 0 && (
                      <div className="flex gap-1 mt-2">
                        {p.subtypes.map(s => (
                          <Badge key={s} variant="outline" className="text-xs capitalize">{s.replace('_', ' ')}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        }) : (
          <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-8 text-center"><BookOpen className="w-10 h-10 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No programs yet.</p></CardContent></Card>
        )}
      </div>
    </div>
  );
}
